import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import App from "./App";
import { AuthProvider } from "./hooks/useAuth";
import { FlowProvider } from "./hooks/useFlow";
import ProtectedRoute from "./ProtectedRoute";
import { detectFlowType, FLOW_TYPES } from "./config/flowConfigs";
import OMIGReward from "./components/rewards/Reward";
import RewardsClaim from "./components/rewards/RewardsClaim";
import MainLayout from "./components/MainLayout";
import AlarmVerificationForm from "./components/nationwide/AlarmVerificationForm";
import AlarmSurvey from "./components/nationwide/AlarmSurvey";
import AlarmFlow from "./components/nationwide/AlarmFlow";
import NationwideWelcome from "./components/nationwide/NationwideWelcome";

const AppWrapper = () => {
  // Detect the current flow type
  const flowType = detectFlowType();

  // Define protected steps for each flow
  const protectedSteps = {};

  // Get default routes based on flow type
  const getDefaultRoute = () => {
    //return "/welcome";
    return "/welcome";
  };

  return (
    <AuthProvider>
      <FlowProvider flowType={flowType}>
        <BrowserRouter>
          <Routes>
            {/* Root redirect */}
            <Route
              path="/"
              element={<Navigate to={getDefaultRoute()} replace />}
            />

            <Route path="/activate" element={<NationwideWelcome />} />

            {/* Rewards (protected by auth) */}
            <Route
              path="/rewards"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <div className="w-full max-w-3xl mx-auto">
                      <OMIGReward />
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/rewards-claim"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <div className="w-full max-w-3xl mx-auto">
                      <RewardsClaim />
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            {/* Legacy verification route */}
            <Route path="./verification" element={<App />} />
            <Route
              path="/alarm/verify"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <div className="w-full max-w-3xl mx-auto">
                      <AlarmFlow />
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/alarm/survey"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <div className="w-full max-w-3xl mx-auto">
                      <AlarmSurvey />
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/alarm/data"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <div className="w-full max-w-3xl mx-auto">
                      <AlarmVerificationForm />
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            {/* Insurance Profile Routes */}
            <Route
              path="/:step"
              element={
                <RouteHandler
                  protectedSteps={protectedSteps[FLOW_TYPES.INSURANCE_PROFILE]}
                  flowType={FLOW_TYPES.INSURANCE_PROFILE}
                >
                  <App />
                </RouteHandler>
              }
            />

            {/* Fallback route */}
            <Route
              path="*"
              element={<Navigate to={getDefaultRoute()} replace />}
            />
          </Routes>
        </BrowserRouter>
      </FlowProvider>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#4ade80",
              secondary: "#fff",
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
    </AuthProvider>
  );
};

// Helper component to conditionally apply protection to routes
function RouteHandler({ children, protectedSteps, flowType }) {
  // Get step from current URL
  const pathname = window.location.pathname;
  const step = pathname.split("/").pop();

  // If step is in protectedSteps list, apply ProtectedRoute
  if (protectedSteps && protectedSteps.includes(step)) {
    return <ProtectedRoute>{children}</ProtectedRoute>;
  }
  // Otherwise, render without protection
  return children;
}

export default AppWrapper;
